<template>
  <div class="schedule-view" data-test="schedule-view">
    <nav class="sv-tabs" data-test="sv-tabs">
      <button type="button" class="sv-tabs__tab sv-tabs__tab--active" data-test="sv-tab-schedule">
        Schedule
      </button>
      <!--
        "Tasks & Follow-ups" is rendered but inert by design (Nilay, 2026-09-19): it is a separate
        project. It is disabled rather than hidden so the shape of the page is honest about what is
        coming, and disabled rather than clickable so nobody reports a dead link as a bug.
      -->
      <button
        type="button"
        class="sv-tabs__tab"
        disabled
        title="Coming soon"
        data-test="sv-tab-tasks"
      >Tasks &amp; Follow-ups</button>
    </nav>

    <ScheduleToolbar
      v-if="date"
      :date="date"
      :today="today"
      :busy="isRefreshing"
      @change-date="goToDate"
      @refresh="refresh"
    />

    <div v-if="day" class="sv-controls" data-test="sv-controls">
      <ScheduleFilterDropdown
        name="provider"
        label="Provider"
        :options="options.providers"
        :selected="filters.providers"
        @change="onFilterChange('providers', $event)"
      />
      <ScheduleFilterDropdown
        name="operatory"
        label="Operatory"
        :options="options.operatories"
        :selected="filters.operatories"
        @change="onFilterChange('operatories', $event)"
      />
      <ScheduleFilterDropdown
        name="status"
        label="Status"
        :options="options.statuses"
        :selected="filters.statuses"
        @change="onFilterChange('statuses', $event)"
      />

      <!-- The gear sits next to Status, per the design. -->
      <ScheduleSettingsPopover
        ref="settings"
        :settings="viewSettings"
        :capabilities="capabilities"
        :confirmation-statuses="day.confirmationStatuses || []"
        :saving="savingSettings"
        :error-message="settingsError"
        @save="onSaveSettings"
        @dismiss="settingsError = ''"
      />

      <!--
        The Schedule / Table view switch (Phase 11), right after the gear — where the design puts it.
        Left out on a phone: a phone never gets the table. Decided HERE, in JS (like the timeline
        below), not by hiding the switch with CSS, so the switch and the view cannot disagree.
      -->
      <ScheduleViewSwitch v-if="!isPhone" :view="view" @change="onChangeView" />

      <span class="sv-controls__spacer"></span>

      <!--
        The freshness indicator.

        ⛔ This is not decoration. Every other surface in Teamio is read once, on purpose, by
        somebody who knows they are reading history. This one is left open on a wall and believed.
        If the office sync stops, the page keeps drawing a perfectly plausible morning that stopped
        being true hours ago — and nobody would notice. A schedule that admits it is behind is far
        better than one that is quietly wrong.

        Healthy sites see a quiet "Live" with no number: the Open Dental heartbeat only arrives
        every ~10 minutes, so a permanent "last synced 8 minutes ago" would read as a warning on a
        page that is working perfectly, and staff would learn to ignore the one line that matters.
      -->
      <span
        class="sv-sync"
        :class="`sv-sync--${syncStatus.tone}`"
        :title="syncStatus.detail"
        data-test="sv-sync-status"
      >
        <span class="sv-sync__dot" aria-hidden="true"></span>
        {{ syncStatus.label }}
      </span>

      <button
        v-if="filtersActive"
        type="button"
        class="sv-controls__clear"
        data-test="sv-clear-filters"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <div v-if="isLoading" class="sv-state" data-test="sv-loading">Loading the schedule…</div>

    <div v-else-if="errorMessage" class="sv-state sv-state--error" data-test="sv-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="sv-state__retry" @click="refresh">Try again</button>
    </div>

    <div v-else-if="unavailable" class="sv-state" data-test="sv-unavailable">
      No Open Dental schedule data has been loaded for this practice yet.
    </div>

    <div v-else-if="!hasOperatories" class="sv-state" data-test="sv-empty">
      No operatories are set up in Open Dental for this practice, so there is nothing to lay the
      day out in.
    </div>

    <template v-else>
      <!--
        A filtered schedule looks exactly like a quiet day. Saying what is hidden is the difference
        between "nobody is coming this afternoon" and "you narrowed this to one hygienist".
      -->
      <ScheduleSummaryBar
        :counts="summary"
        :appointments="filteredDay.appointments"
        :alert-payload="alertPayload"
        :filters="filters"
        @toggle-status="toggleIn('statuses', $event)"
        @toggle-alert="toggleIn('alerts', $event)"
      />

      <p v-if="hiddenNotice" class="sv-hidden" data-test="sv-hidden-notice">{{ hiddenNotice }}</p>

      <div
        v-if="!filteredDay.operatories.length"
        class="sv-state"
        data-test="sv-no-match"
      >
        No operatories match the current filter.
        <button type="button" class="sv-state__retry" @click="clearFilters">Clear filters</button>
      </div>

      <!--
        The Table view (Phase 11): one row per appointment, every row carrying its visit checklist.
        It is handed the page's OWN `checklistByAptNum` — the one map the blocks, the chip and the
        card read — and the FILTERED day, so a row can never say more than its block does. Ticks go
        through the same store action as the card's. `effectiveView` is never 'table' on a phone.
      -->
      <ScheduleTableView
        v-else-if="effectiveView === 'table'"
        :appointments="filteredAppointments"
        :operatories="filteredDay.operatories"
        :unfiltered-day="day"
        :providers="day.providers || []"
        :checklists="checklistByAptNum"
        :checks="checks"
        :bucket-map="bucketMap"
        :alert-payload="alertPayload"
        :sort="tableSort"
        :date="date || ''"
        :timezone="timezone || ''"
        :date-format="panelDateFormat"
        :practice-name="practiceName"
        :filters-active="filtersActive"
        @select-appointment="onSelectAppointment"
        @toggle="onToggleCheck"
        @change-sort="onChangeSort"
        @clear-filters="clearFilters"
      />

      <!--
        Phone = one column of cards (Phase 6); iPad and desktop = the operatory grid. Same props,
        so the page does not care which is on screen. Decided in JS rather than by hiding one with
        CSS: rendering both would double every block, card and alert icon on the page.
      -->
      <ScheduleTimeline
        v-else-if="isPhone"
        :day="filteredDay"
        :unfiltered-day="day"
        :view-settings="viewSettings"
        :bucket-map="bucketMap"
        :alert-payload="alertPayload"
        :now-minutes="nowMinutes"
        :is-today="date === today"
        :checklists="checklistByAptNum"
        :checklist-mode="checklistMode"
        @select-appointment="onSelectAppointment"
        @card-enter="onCardEnter"
        @card-leave="onCardLeave"
        @alert-focus="onAlertFocus"
        @open-checklist="onOpenChecklist"
      />

      <ScheduleGrid
        v-else
        :day="filteredDay"
        :unfiltered-day="day"
        :view-settings="viewSettings"
        :bucket-map="bucketMap"
        :alert-payload="alertPayload"
        :now-minutes="nowMinutes"
        :is-today="date === today"
        :checklists="checklistByAptNum"
        :checklist-mode="checklistMode"
        @select-appointment="onSelectAppointment"
        @card-enter="onCardEnter"
        @card-leave="onCardLeave"
        @alert-focus="onAlertFocus"
        @open-checklist="onOpenChecklist"
      />
    </template>

    <!--
      THE visit checklist's one layer (Phase 10e). Exactly one on the page: it holds which card is
      open, and every block and phone card only reports the pointer to it. It is always mounted —
      it draws nothing until a card opens — because it also owns the hover/touch detection that
      tells the blocks whether to draw a chip, and reports it through `mode`.
    -->
    <ChecklistLayer
      ref="checklistLayer"
      :appointments="filteredAppointments"
      :unfiltered-day="day"
      :bucket-map="bucketMap"
      :checklists="checklistByAptNum"
      :alert-payload="alertPayload"
      :date="date || ''"
      @mode="checklistMode = $event"
      @open-tab="onChecklistOpenTab"
    />

    <!--
      The Patient Panel (Phase 7). Everything it is given is LIVE — the appointment is looked up by
      AptNum in the current day on every render, and the alerts are the same payload the blocks
      read — so a patient who arrives while the panel is open changes status in the panel too.
      Which patient is open lives in the patientPanel store, so this host can later move to
      Layout.vue for the header patient search without the panel changing.
    -->
    <PatientPanel
      :appointment="panelAppointment"
      :appointment-meta="panelAppointmentMeta"
      :alert-payload="alertPayload"
      :view-settings="viewSettings"
      :timezone="timezone || ''"
      :date-format="panelDateFormat"
    />

    <!--
      This page's ONE vue2-slideout-panel outlet — the panel's Send Form / Text / Email and Create Task
      open through it ($showPanel). Exactly one: every mounted outlet renders every panel, so a second
      one draws a twin (ChatContainer.vue explains the Practice Pulse incident). The wrapper lifts the
      slide-outs (z-index ~101 of their own) above the Patient Panel (1035), which opened them.
    -->
    <div class="sv-slideout-host" data-test="sv-slideout-host">
      <slideout-panel></slideout-panel>
    </div>
  </div>
</template>

<script>
/**
 * Schedule View — the office's day, laid out by operatory the way Open Dental draws it.
 *
 * Plan: docs/plans/2026-09-19-schedule-view-plan.md
 * Rules ledger: teamio-serverless-api/schedule-view-service/docs/schedule-view.md
 *
 * The grid on an iPad or desktop, the timeline on a phone (Phase 6), with alert icons, the
 * settings popover and live refresh on both. Clicking a block or a card opens the Patient Panel
 * (Phase 7, src/components/PatientPanel/).
 *
 * ⛔ DO NOT declare props named `patNum`, `siteID` or `loading` on this page or any Schedule View
 * component: mixin.js defines computeds with those names and a computed SILENTLY REPLACES a
 * same-named prop. Guarded by cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import { mapState, mapGetters } from 'vuex';
import ScheduleToolbar from './components/ScheduleToolbar.vue';
import ScheduleGrid from './components/ScheduleGrid.vue';
import ScheduleTimeline from './components/ScheduleTimeline.vue';
import ScheduleFilterDropdown from './components/ScheduleFilterDropdown.vue';
import ScheduleSettingsPopover from './components/ScheduleSettingsPopover.vue';
import ScheduleSummaryBar from './components/ScheduleSummaryBar.vue';
import ScheduleViewSwitch from './components/ScheduleViewSwitch.vue';
import ScheduleTableView from './components/ScheduleTableView.vue';
import ChecklistLayer from './components/ChecklistLayer.vue';
import { todayForSite, nowMinutesForSite } from '@/store/scheduleView';
import { buildBucketMap, resolveBucket, statusLabel, isBroken } from '@/utils/scheduleView/statusBuckets';
import PatientPanel from '@/components/PatientPanel/PatientPanel.vue';
import { dateFormatFor } from '@/utils/taskDraft';
import { applyFilters, filterOptions, hasActiveFilters } from '@/utils/scheduleView/filters';
import {
  summaryCounts, visibleAlertKeys, blockAlertKeys, alertsForPatient, describeAlert,
} from '@/utils/scheduleView/alerts';
import { checklistsForDay } from '@/utils/scheduleView/checklist';
import { ALERT_KEYS } from '@/utils/scheduleView/settingsForm';
import { describeSync, PULSE_INTERVAL_MS } from '@/utils/scheduleView/liveness';
import { PHONE_MEDIA_QUERY, appointmentProviderNum } from '@/utils/scheduleView/layout';

/** How often the NOW line moves. A minute is as precise as a 76px/hour grid can show. */
const NOW_TICK_MS = 60000;
/**
 * Alerts refresh more slowly than the grid, deliberately.
 *
 * The schedule changes minute to minute — people are confirmed, arrive, get seated. The things
 * alerts measure do not: a family balance, an aged claim and an overdue recall are the same at
 * 10:02 as at 10:00. These are five queries against procedurelog and claim, which are the
 * expensive tables on a mature practice, so running them at the grid's cadence would multiply that
 * cost for no new information.
 */
const ALERT_REFRESH_MS = 600000;

export default {
  name: 'ScheduleView',

  components: {
    ScheduleToolbar, ScheduleGrid, ScheduleTimeline, ScheduleFilterDropdown, ScheduleSettingsPopover,
    ScheduleSummaryBar, ScheduleViewSwitch, ScheduleTableView, PatientPanel, ChecklistLayer,
  },

  data() {
    return {
      nowMinutes: 0,
      isRefreshing: false,
      pulseTimer: null,
      nowTimer: null,
      /** A failed settings save's reason, shown inside the popover rather than as a toast. */
      settingsError: '',
      /** When the alerts were last fetched, so they can refresh on their own slower clock. */
      lastAlertFetch: 0,
      /**
       * True until the first load has settled. The very first fetch has to guess "today" from the
       * browser, because only the response carries the practice's timezone; this flag lets that
       * one load correct itself without re-fetching on every later navigation.
       */
      wasDefaultLoad: true,
      /** True on a phone-width window: draw the timeline instead of the grid. See PHONE_MAX_WIDTH. */
      isPhone: false,
      /** The live media-query listener, kept so it can be removed. */
      viewportWatch: null,
      /**
       * How the visit checklist opens: 'hover' (a mouse) | 'pop' | 'sheet' (touch). Decided by the
       * ChecklistLayer, which owns the media queries, and reported up so the blocks know whether to
       * draw a chip. 'hover' until it reports, which draws no chip — a mouse is the common case.
       */
      checklistMode: 'hover',
    };
  },

  computed: {
    ...mapState('scheduleView', {
      date: state => state.date,
      day: state => state.day,
      isLoading: state => state.loading,
      errorMessage: state => state.error,
      unavailable: state => state.unavailable,
      filters: state => state.filters,
      savingSettings: state => state.savingSettings,
      checks: state => state.checks,
      view: state => state.view,
      tableSort: state => state.tableSort,
    }),

    ...mapGetters('scheduleView', ['capabilities', 'alertsForDate', 'sync', 'taskLinesByPatNum']),

    /** Alerts, but only ever the ones belonging to the day on screen. See the store's getter. */
    alertPayload() {
      return this.alertsForDate;
    },

    /**
     * Every patient on the day, for the live `task` listeners.
     *
     * ⛔ Deduped. A patient booked twice would otherwise occupy two of the thirty slots in an
     * `array-contains-any` query, and on a busy day that is how a practice quietly ends up needing
     * a fourth listener for patients it is already watching.
     *
     * Taken from the UNFILTERED day: the Provider/Operatory/Status filters narrow what is DRAWN,
     * and re-subscribing every time somebody flicks a filter would tear down and rebuild every
     * listener for no gain.
     */
    dayPatNums() {
      const appts = (this.day && this.day.appointments) || [];
      const seen = [];
      appts.forEach((appt) => {
        const p = appt && appt.PatNum;
        if (p === null || p === undefined) return;
        const key = String(p);
        if (seen.indexOf(key) === -1) seen.push(key);
      });
      return seen;
    },

    /** The freshness indicator's words. Pure, so the wording is testable without mounting. */
    syncStatus() {
      return describeSync(this.sync);
    },

    /** The version the last pulse reported, used to re-baseline after a manual refresh. */
    pulseVersion() {
      const pulse = this.$store.state.scheduleView.pulse;
      return (pulse && pulse.date === this.date) ? pulse.v : null;
    },

    /**
     * What is actually drawn: the remembered view, except that a phone never gets the table. The
     * stored choice is NOT changed for a phone — turning the iPad back restores the table.
     */
    effectiveView() {
      return this.isPhone ? 'schedule' : this.view;
    },

    /** The site's timezone, once the first payload has arrived. */
    timezone() {
      return this.day ? this.day.timezone : null;
    },

    /**
     * The practice's name, for the printed sheet's kicker; '' when the settings are not loaded.
     *
     * Read off the store's settings rows ({ Property, PropertyValue }) directly. NOT through the
     * global mixin's `allSettings`: this page does not include that mixin (it is opt-in per
     * component, `mixins: [mixin]`), and `allSettings` also needs a bare global `_`.
     */
    practiceName() {
      const rows = this.$store.state.customer && this.$store.state.customer.settings;
      if (!Array.isArray(rows)) return '';
      const row = rows.find(r => r && r.Property === 'GENERAL_PRACTICE_NAME');
      return row && row.PropertyValue ? String(row.PropertyValue).trim() : '';
    },

    today() {
      return todayForSite(this.timezone);
    },

    /**
     * Settings always come from the payload the server resolved — the page never keeps its own
     * copy of the defaults, or the two would drift and the office would be told one thing while
     * seeing another.
     */
    viewSettings() {
      return this.day && this.day.settings ? this.day.settings : null;
    },

    bucketMap() {
      if (!this.day) return {};
      return buildBucketMap(
        this.day.confirmationStatuses,
        this.viewSettings ? this.viewSettings.confirmationBuckets : {}
      );
    },

    hasOperatories() {
      return !!(this.day && this.day.operatories && this.day.operatories.length);
    },

    options() {
      return filterOptions(this.day);
    },

    filtersActive() {
      return hasActiveFilters(this.filters);
    },

    /** The day the grid actually draws. Identical to `day` when nothing is filtered. */
    filtered() {
      return applyFilters(
        this.day,
        this.filters,
        appt => resolveBucket(appt, this.bucketMap),
        appt => visibleAlertKeys(this.alertPayload, appt.PatNum, ALERT_KEYS)
      );
    },

    filteredDay() {
      return this.filtered.day;
    },

    filteredAppointments() {
      return (this.filteredDay && this.filteredDay.appointments) || [];
    },

    /**
     * Every visit checklist for the day on screen, built ONCE per render and handed to the blocks
     * (the chip), the phone cards and the layer (the card) — so the icon row, the chip and the card
     * can never give three answers.
     *
     * ⛔ Built from `blockAlertKeys`, the same function the blocks call for their icons, over the
     * FILTERED day (what is actually drawn). A checklist built from the raw alert payload would list
     * an alert the office switched off, or one whose rule failed, beside a block that shows no icon.
     * Re-derived whenever a tick lands (`checks`), so the chip and the progress bar move with it.
     */
    checklistByAptNum() {
      return checklistsForDay({
        appointments: this.filteredAppointments,
        payload: this.alertPayload,
        viewSettings: this.viewSettings,
        order: ALERT_KEYS,
        tasksByPatNum: this.taskLinesByPatNum,
        checks: this.checks,
        today: this.today,
        deps: { blockAlertKeys, alertsForPatient, describe: describeAlert },
      });
    },

    /**
     * Counts for the summary strip, taken from the FILTERED day.
     *
     * So the strip always describes what is actually on screen. Counting the unfiltered day would
     * mean clicking "6 unconfirmed" narrows the grid to six blocks while the card keeps saying
     * six out of a total nobody can see any more — the numbers and the grid would stop agreeing.
     */
    summary() {
      return summaryCounts(
        this.filteredDay ? this.filteredDay.appointments : [],
        this.alertPayload,
        appt => resolveBucket(appt, this.bucketMap)
      );
    },

    /**
     * The appointment the Patient Panel was opened from, looked up LIVE in the unfiltered day.
     * Null when it is no longer on this day (moved by the office, or the user changed date) — the
     * panel then keeps the patient and drops only the "This appointment" card.
     */
    panelAppointment() {
      const aptNum = this.$store.state.patientPanel.openAptNum;
      if (!aptNum || !this.day) return null;
      return (this.day.appointments || []).find(a => a.AptNum === aptNum) || null;
    },

    /** Chair, provider and status for that appointment, resolved the way the block resolves them. */
    panelAppointmentMeta() {
      const appt = this.panelAppointment;
      if (!appt) return null;
      const op = (this.day.operatories || []).find(o => o.OperatoryNum === appt.Op);
      const provNum = appointmentProviderNum(appt);
      const prov = (this.day.providers || []).find(p => p.ProvNum === provNum);
      const bucket = resolveBucket(appt, this.bucketMap);
      return {
        opName: op ? (op.OpName || op.Abbrev || '') : '',
        providerName: prov ? `${prov.FName || ''} ${prov.LName || ''}`.trim() || prov.Abbr || '' : '',
        bucket,
        statusText: statusLabel(appt, bucket),
        broken: isBroken(appt),
      };
    },

    /** MM/DD/YYYY, or DD/MM/YYYY for an office that writes day first — Teamio's one rule. */
    panelDateFormat() {
      return dateFormatFor(this.timezone);
    },

    hiddenNotice() {
      const { hiddenAppointments, hiddenOperatories } = this.filtered;
      if (!hiddenAppointments && !hiddenOperatories) return '';
      const parts = [];
      if (hiddenAppointments) {
        parts.push(`${hiddenAppointments} appointment${hiddenAppointments === 1 ? '' : 's'}`);
      }
      if (hiddenOperatories) {
        parts.push(`${hiddenOperatories} operator${hiddenOperatories === 1 ? 'y' : 'ies'}`);
      }
      return `${parts.join(' and ')} hidden by the current filter.`;
    },
  },

  created() {
    // Before the first paint, so a phone never draws a grid for one frame and then swaps it.
    this.watchViewport();

    // Per-browser narrowing, restored before the first paint so the grid does not visibly reflow
    // from "everything" to "what this person actually looks at".
    this.$store.dispatch('scheduleView/loadFilters');

    // The remembered view (schedule / table) and the table's sort, the same way and for the same
    // reason: restored before the first paint, so the page does not flash the grid and then swap.
    this.$store.dispatch('scheduleView/loadTablePrefs');

    // The day is resolved against the SITE's timezone, which only the payload knows. The first
    // load therefore asks for the browser's today and re-resolves once the timezone is known —
    // rather than blocking the first paint on a settings round trip.
    this.goToDate(todayForSite(null));
  },

  watch: {
    // The checklist layer's own rule: a card closes when the view changes. In the table no block
    // exists to call enter(), so nothing can open one — this only clears one left open by the grid.
    effectiveView() {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.close();
    },
  },

  mounted() {
    this.tickNow();
    this.nowTimer = setInterval(this.tickNow, NOW_TICK_MS);
    // The liveness loop. This replaced a blind two-minute `/day` refetch: the office leaves this
    // page open on an operatory iPad and expects it to track Open Dental without anyone pressing
    // Refresh, and two minutes is long enough that staff would reach for the button first.
    this.pulseTimer = setInterval(this.livePoll, PULSE_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // The office's own machine, telling us the moment a day changes (Phase 3Lb). Purely an
    // accelerator: it feeds the same refetch path as the timer, and an office without it — old
    // exe, setting off, firewalled Firestore — is slower, never broken. The poll above is what
    // guarantees correctness; this is what makes it feel instant.
    this.$store.dispatch('scheduleView/watchSignal');
  },

  beforeDestroy() {
    if (this.nowTimer) clearInterval(this.nowTimer);
    if (this.pulseTimer) clearInterval(this.pulseTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.unwatchViewport();
    this.$store.dispatch('scheduleView/unwatchSignal');
    this.$store.dispatch('scheduleView/unwatchTasks');
    // Leaving the page closes the panel and releases its Firestore listener. Left open, it would
    // reappear over whatever page the user comes back to, showing a patient they have moved on from.
    this.$store.dispatch('patientPanel/close');
  },

  methods: {
    async goToDate(date) {
      await this.$store.dispatch('scheduleView/fetchDay', { date });
      this.tickNow();

      // NOT awaited, on purpose. The grid is already drawable; the icons decorate it when they
      // arrive. Awaiting would make a slow claims query on a big practice the reason nobody can
      // see their schedule — which is precisely what splitting the two endpoints was for.
      this.lastAlertFetch = Date.now();
      this.$store.dispatch('scheduleView/fetchAlerts', { date });

      // The `task` alert is LIVE rather than fetched, so it is re-pointed at the new day's
      // patients here. Re-watching unsubscribes first, which matters on a date change: leaving
      // yesterday's listeners running would keep yesterday's patients' icons on screen and go on
      // paying for the reads.
      this.$store.dispatch('scheduleView/watchTasks', this.dayPatNums);

      // Prime the live loop NOW rather than letting the timer get to it, for two reasons found by
      // watching the real page on dev:
      //   1. the freshness indicator has nothing to report until the first pulse lands, so the
      //      page opened saying "Sync status unknown" for a full interval — an alarming thing to
      //      show somebody for thirty seconds on a schedule that is working perfectly;
      //   2. the first tick is spent ADOPTING the day's version, so the first real comparison did
      //      not happen until the SECOND tick — a change made just after load took ~60s to show,
      //      not the ~30s the interval implies.
      // Same single refetch path as everything else, and not awaited: the grid is already drawn.
      this.$store.dispatch('scheduleView/liveTick');

      // Now that the payload has told us the practice's timezone, check that the browser's idea
      // of "today" agreed with the practice's. It will not for a user in another timezone near
      // midnight, and silently showing them the wrong day is the failure this catches.
      const siteToday = todayForSite(this.timezone);
      if (date !== siteToday && this.wasDefaultLoad) {
        this.wasDefaultLoad = false;
        await this.$store.dispatch('scheduleView/fetchDay', { date: siteToday });
      }
    },

    /**
     * The manual Refresh button.
     *
     * Kept even though the page now follows Open Dental by itself, and deliberately made
     * UNCONDITIONAL: it refetches the day whatever the pulse says. Somebody pressing Refresh is
     * telling us they do not believe the screen, and answering that with "the version is
     * unchanged, so I did nothing" is how a page loses an office's trust for good — especially
     * given the one gap the pulse has (a blockout re-assigned between chairs leaves no stamp).
     */
    async refresh() {
      this.isRefreshing = true;
      try {
        await this.$store.dispatch('scheduleView/fetchDay', { date: this.date, silent: true });
        // Re-baseline, so the next pulse compares against what is now drawn rather than
        // immediately refetching the day this just loaded.
        await this.$store.dispatch('scheduleView/fetchPulse', { date: this.date });
        this.$store.commit('scheduleView/setDrawnVersion', this.pulseVersion);
      } finally {
        this.isRefreshing = false;
      }
      // Alerts follow the schedule but on their own clock (see ALERT_REFRESH_MS): the grid is
      // what changes minute to minute as people are confirmed and seated. A balance does not.
      if (Date.now() - this.lastAlertFetch >= ALERT_REFRESH_MS) this.refreshAlerts();
    },

    refreshAlerts() {
      this.lastAlertFetch = Date.now();
      this.$store.dispatch('scheduleView/fetchAlerts', { date: this.date });
    },

    /**
     * One tick of the liveness loop, but only while the tab is actually being looked at.
     *
     * Polling a hidden tab spends the practice's budget on a screen nobody is reading — an iPad
     * face-down on a counter, or a tab behind twelve others. Nothing is lost by pausing, because
     * becoming visible triggers an immediate check (below) rather than waiting for the next tick.
     *
     * The single-flight guard lives in the store action, not here, so that the timer, the focus
     * handler and the manual button cannot each invent their own version of it.
     */
    livePoll() {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (this.isLoading) return;
      this.$store.dispatch('scheduleView/liveTick');
    },

    /**
     * Coming back to the tab checks IMMEDIATELY rather than waiting out the poll interval.
     *
     * This is the moment staleness is most likely and most visible: somebody has walked back to
     * the iPad to look something up, and whatever happened while the screen was off is exactly
     * what they are about to read.
     */
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.livePoll();
    },

    tickNow() {
      this.nowMinutes = nowMinutesForSite(this.timezone);
    },

    /**
     * Follow the window across the phone breakpoint — rotating a phone, or narrowing a desktop
     * window, swaps grid and timeline live. Nothing is refetched: both draw the same store state.
     */
    watchViewport() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
      const mql = window.matchMedia(PHONE_MEDIA_QUERY);
      const onChange = () => { this.isPhone = !!mql.matches; };
      // Safari < 14 — older operatory iPads — only has the deprecated addListener.
      if (mql.addEventListener) mql.addEventListener('change', onChange);
      else if (mql.addListener) mql.addListener(onChange);
      this.viewportWatch = { mql, onChange };
      onChange();
    },

    unwatchViewport() {
      const watch = this.viewportWatch;
      if (!watch) return;
      if (watch.mql.removeEventListener) watch.mql.removeEventListener('change', watch.onChange);
      else if (watch.mql.removeListener) watch.mql.removeListener(watch.onChange);
      this.viewportWatch = null;
    },

    onFilterChange(key, values) {
      this.$store.dispatch('scheduleView/updateFilters', { ...this.filters, [key]: values });
    },

    /** Summary cards toggle rather than replace, so two cards can be combined. */
    toggleIn(key, value) {
      const current = this.filters[key] || [];
      const next = current.indexOf(value) === -1
        ? current.concat([value])
        : current.filter(v => v !== value);
      this.onFilterChange(key, next);
    },

    clearFilters() {
      this.$store.dispatch('scheduleView/updateFilters', {
        providers: [], operatories: [], statuses: [], alerts: [],
      });
    },

    async onSaveSettings(form) {
      this.settingsError = '';
      const result = await this.$store.dispatch('scheduleView/saveSettings', { form });
      if (result && result.ok) {
        if (this.$refs.settings) this.$refs.settings.close();
        this.$toast.success('Schedule View settings saved for this office.');
        return;
      }
      // Shown inside the popover, beside the form they can still correct — not as a toast that
      // appears after the panel has been torn down.
      this.settingsError = (result && result.errors && result.errors[0])
        || 'Could not save those settings. Please try again.';
    },

    /**
     * A block (or a phone card) was clicked: open that patient in the Patient Panel.
     *
     * The capabilities decide which optional sources the panel fetches at all — a site without
     * Insurance Verification or Tx Presenter gets no request for them, not a request that comes
     * back empty. "Today" is the SITE's today, which is what "upcoming appointments" is measured
     * from on Patient Profile too.
     *
     * `tab` (optional) lands the panel on that tab — the visit checklist's category arrows pass one.
     * A plain click passes none and opens exactly as before.
     */
    onSelectAppointment(appt, tab) {
      if (!appt || !appt.PatNum) return;
      const caps = this.capabilities || {};
      const request = {
        patNum: appt.PatNum,
        aptNum: appt.AptNum,
        today: this.today,
        capabilities: { insverify: !!caps.insverify, txsign: !!caps.txsign },
      };
      if (typeof tab === 'string' && tab) request.tab = tab;
      this.$store.dispatch('patientPanel/open', request);
    },

    // ── the visit checklist: blocks and cards report, the one layer decides ─────────────────────
    onCardEnter(e) {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.enter(e);
    },

    onCardLeave() {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.leave();
    },

    onAlertFocus(e) {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.focusAlert(e);
    },

    onOpenChecklist(e) {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.toggle(e);
    },

    /** A category arrow (or "Open patient profile") on the card: open the panel on that tab. */
    onChecklistOpenTab({ appt, tab } = {}) {
      this.onSelectAppointment(appt, tab);
    },

    // ── the Table view ──────────────────────────────────────────────────────────────────────────
    onChangeView(view) {
      this.$store.dispatch('scheduleView/updateView', view);
    },

    onChangeSort(sort) {
      this.$store.dispatch('scheduleView/updateTableSort', sort);
    },

    /**
     * A to-do was ticked in the table: the SAME store action as the card's tick, so one tick flips
     * both. The store is free of UI, so a failed save is reported here (the tick has already been
     * rolled back) — in the same words as ChecklistLayer.onToggle.
     */
    async onToggleCheck({ aptNum, itemKey, checked } = {}) {
      if (aptNum === null || aptNum === undefined || !itemKey) return;
      const result = await this.$store.dispatch('scheduleView/toggleCheck', { aptNum, itemKey, checked: !!checked });
      if (result && result.ok === false && this.$toast && typeof this.$toast.error === 'function') {
        this.$toast.error(result.error || 'Could not save the checkmark.');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'tokens';

.schedule-view {
  // Hanken Grotesk is already loaded app-wide (src/styles/theme.scss) and is what the newer Teamio
  // surfaces use — the design export's Plus Jakarta Sans is deliberately NOT added.
  font-family: 'Hanken Grotesk', 'Montserrat', sans-serif;
  padding: 16px;
}

// A stacking context one above the Patient Panel (1035), so the slide-outs it opens — which draw at
// ~101 inside it — land on top of the panel instead of underneath. Below Bootstrap's modal layer
// (1040), so a modal a slide-out opens is still on top of the slide-out.
.sv-slideout-host {
  position: relative;
  z-index: 1036;
}

.sv-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
  border-bottom: 1px solid $sv-line;
}

.sv-tabs__tab {
  border: 0;
  background: none;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  color: $sv-ink-3;
  border-bottom: 2px solid transparent;
  cursor: pointer;

  &:disabled { opacity: 0.45; cursor: default; }
  &--active { color: $sv-brand; border-bottom-color: $sv-brand; }
}

.sv-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.sv-controls__spacer {
  flex: 1 1 auto;
}

.sv-controls__clear {
  height: 34px;
  padding: 0 12px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  color: $sv-brand;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

/**
 * The freshness indicator.
 *
 * Weighted to be read at a glance from across a room and IGNORED when healthy. "Live" is quiet —
 * muted ink, small dot, no border — because a badge shouting that everything is fine trains people
 * to stop looking at it. "Stale" gets the practice's warning colour and a filled chip, because by
 * then the number IS the message.
 */
.sv-sync {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border-radius: $sv-radius;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: default;

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    flex: 0 0 auto;
  }

  &--live {
    color: $sv-status-confirmed;
    background: transparent;
  }

  &--stale {
    color: #a5292e;
    background: #fdecec;
    // The one state that earns a border: it has to survive being glanced at from the far side of
    // an operatory, where the colour alone is not enough on a bright screen.
    border: 1px solid #f3c6c6;
  }

  &--unknown {
    color: $sv-ink-3;
    background: transparent;
  }
}

.sv-hidden {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: $sv-ink-3;
}

.sv-state {
  border: 1px dashed $sv-line;
  border-radius: $sv-radius-lg;
  background: $sv-surface;
  padding: 24px;
  font-size: 14px;
  color: $sv-ink-3;
  max-width: 640px;

  &--error { border-style: solid; border-color: #e5484d; color: #a5292e; }
}

.sv-state__retry {
  margin-top: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid currentColor;
  border-radius: $sv-radius;
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

// Phone: no side padding of our own — the app layout already leaves a 20px gutter, and with
// Teamio's sidebar taking 47px of a 390px screen, a second gutter comes straight off the cards.
@media (max-width: $sv-phone-max) {
  .schedule-view { padding: 8px 0 0; }
}
</style>
